import React, { useState, useMemo } from 'react';
import './CustomerForm.css';

const CustomerForm = ({ initialData, onSubmit }) => {
    const [formData, setFormData] = useState({
        name: initialData?.name || '',
        phone: initialData?.phone || '',
        email: initialData?.email || '',
        address: initialData?.address || '',
        document_number: initialData?.document_number || '',
        canal: initialData?.canal || '',
        subcanal: initialData?.subcanal || '',
        tipo_cliente: initialData?.tipo_cliente || '',
        departamento: initialData?.departamento || '',
        provincia: initialData?.provincia || '',
        distrito: initialData?.distrito || '',
        rubro: initialData?.rubro || '',
    });

    const [showDepartamentoDropdown, setShowDepartamentoDropdown] = useState(false);

    const departamentos = [
        'Amazonas', 'Áncash', 'Apurímac', 'Arequipa', 'Ayacucho', 'Cajamarca', 'Callao', 
        'Cusco', 'Huancavelica', 'Huánuco', 'Ica', 'Junín', 'La Libertad', 'Lambayeque', 
        'Lima', 'Loreto', 'Madre de Dios', 'Moquegua', 'Pasco', 'Piura', 'Puno', 
        'San Martín', 'Tacna', 'Tumbes', 'Ucayali'
    ];

    const filteredDepartamentos = useMemo(() => {
        if (!formData.departamento) return departamentos;
        return departamentos.filter(dep =>
            dep.toLowerCase().includes(formData.departamento.toLowerCase())
        );
    }, [formData.departamento]);

    const provinciasPorDepartamento = {
        'Lima': ['Lima', 'Barranca', 'Cañete', 'Huaral', 'Huarochirí', 'Huaura'],
        'Arequipa': ['Arequipa', 'Camaná', 'Caylloma', 'Islay'],
        'Cusco': ['Cusco', 'Anta', 'Calca', 'Urubamba'],
        'La Libertad': ['Trujillo', 'Pacasmayo', 'Ascope'],
        'Piura': ['Piura', 'Sullana', 'Talara'],
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const newData = { ...prev, [name]: value };
            if (name === 'departamento') {
                newData.provincia = '';
                newData.distrito = '';
            }
            return newData;
        });
    };

    const handleDepartamentoSearch = (e) => {
        handleChange(e);
        setShowDepartamentoDropdown(true);
    };

    const selectDepartamento = (dep) => {
        setFormData(prev => ({ ...prev, departamento: dep, provincia: '', distrito: '' }));
        setShowDepartamentoDropdown(false);
    };

    const [formError, setFormError] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.name.trim()) {
            setFormError('El nombre del cliente o razón social es obligatorio.');
            return;
        }

        const doc = (formData.document_number || '').trim();
        if (doc) {
            if (!/^\d+$/.test(doc)) {
                setFormError('El número de documento debe contener solo dígitos.');
                return;
            }
            if (doc.length !== 8 && doc.length !== 11) {
                setFormError('El documento debe ser un DNI válido (8 dígitos) o RUC válido (11 dígitos).');
                return;
            }
            if (doc.length === 11 && !formData.address.trim()) {
                setFormError('Para clientes con RUC (Facturas), la dirección fiscal es obligatoria según normativa SUNAT.');
                return;
            }
        }

        setFormError('');
        onSubmit(formData);
    };

    const docLen = (formData.document_number || '').trim().length;

    return (
        <form onSubmit={handleSubmit} className="customer-form-horizontal">
            {formError && (
                <div style={{
                    background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
                    padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontSize: '0.9rem'
                }}>
                    ⚠️ {formError}
                </div>
            )}
            <div className="form-two-columns">
                
                {/* ============= COLUMNA IZQUIERDA ============= */}
                <div className="form-column-left">
                    
                    {/* Sección: Datos Básicos */}
                    <div className="form-section">
                        <h3 className="section-title">📋 Información Básica</h3>
                        
                        <div className="form-group">
                            <label>Nombre Completo / Razón Social <span className="required">*</span></label>
                            <input 
                                type="text" 
                                name="name" 
                                value={formData.name} 
                                onChange={handleChange} 
                                required 
                                placeholder="Ej: Juan Pérez o Distribuidora Farma S.A.C."
                            />
                        </div>
                        
                        <div className="form-group">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <label>DNI (8 dígitos) / RUC (11 dígitos)</label>
                                {docLen === 8 && <span style={{ color: '#16a34a', fontSize: '0.75rem', fontWeight: 600 }}>✓ DNI (Boletas)</span>}
                                {docLen === 11 && <span style={{ color: '#2563eb', fontSize: '0.75rem', fontWeight: 600 }}>✓ RUC (Facturas)</span>}
                            </div>
                            <input 
                                type="text" 
                                name="document_number" 
                                value={formData.document_number} 
                                onChange={handleChange}
                                placeholder="8 dígitos (DNI) u 11 dígitos (RUC)"
                                maxLength="11"
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>Teléfono</label>
                            <input 
                                type="text" 
                                name="phone" 
                                value={formData.phone} 
                                onChange={handleChange}
                                placeholder="999 999 999"
                                maxLength="15"
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>Email</label>
                            <input 
                                type="email" 
                                name="email" 
                                value={formData.email} 
                                onChange={handleChange}
                                placeholder="cliente@empresa.com"
                            />
                        </div>
                    </div>

                    {/* Sección: Clasificación */}
                    <div className="form-section">
                        <h3 className="section-title">🏷️ Clasificación</h3>
                        
                        <div className="form-group">
                            <label>Tipo de Cliente</label>
                            <select name="tipo_cliente" value={formData.tipo_cliente} onChange={handleChange}>
                                <option value="">Seleccionar</option>
                                <option value="mayorista">Mayorista</option>
                                <option value="minorista">Minorista</option>
                                <option value="distribuidor">Distribuidor</option>
                                <option value="consumidor_final">Consumidor Final</option>
                            </select>
                        </div>
                        
                        <div className="form-group">
                            <label>Canal</label>
                            <select name="canal" value={formData.canal} onChange={handleChange}>
                                <option value="">Seleccionar</option>
                                <option value="tradicional">Tradicional</option>
                                <option value="moderno">Moderno</option>
                                <option value="institucional">Institucional</option>
                                <option value="ecommerce">E-commerce</option>
                            </select>
                        </div>
                        
                        <div className="form-group">
                            <label>Subcanal</label>
                            <input 
                                type="text" 
                                name="subcanal" 
                                value={formData.subcanal} 
                                onChange={handleChange} 
                                placeholder="Bodega, Supermercado..."
                                list="subcanal-list"
                            />
                            <datalist id="subcanal-list">
                                <option value="Bodega" />
                                <option value="Supermercado" />
                                <option value="Mercado" />
                                <option value="Farmacia" />
                            </datalist>
                        </div>
                        
                        <div className="form-group">
                            <label>Rubro</label>
                            <input 
                                type="text" 
                                name="rubro" 
                                value={formData.rubro} 
                                onChange={handleChange} 
                                placeholder="Abarrotes, Tecnología..."
                                list="rubro-list"
                            />
                            <datalist id="rubro-list">
                                <option value="Abarrotes" />
                                <option value="Farmacia" />
                                <option value="Tecnología" />
                                <option value="Construcción" />
                            </datalist>
                        </div>
                    </div>
                </div>

                {/* ============= COLUMNA DERECHA ============= */}
                <div className="form-column-right">
                    
                    {/* Sección: Ubicación */}
                    <div className="form-section">
                        <h3 className="section-title">📍 Ubicación</h3>
                        
                        <div className="form-group">
                            <label>Departamento</label>
                            <div className="search-select-container">
                                <input 
                                    type="text" 
                                    name="departamento"
                                    value={formData.departamento}
                                    onChange={handleDepartamentoSearch}
                                    onFocus={() => setShowDepartamentoDropdown(true)}
                                    placeholder="Buscar departamento..."
                                    autoComplete="off"
                                />
                                {showDepartamentoDropdown && (
                                    <div className="search-dropdown">
                                        {filteredDepartamentos.length > 0 ? (
                                            filteredDepartamentos.map((dep, idx) => (
                                                <div 
                                                    key={idx} 
                                                    className="dropdown-item" 
                                                    onClick={() => selectDepartamento(dep)}
                                                >
                                                    {dep}
                                                </div>
                                            ))
                                        ) : (
                                            <div className="dropdown-item disabled">Sin resultados</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        <div className="form-group">
                            <label>Provincia</label>
                            <input 
                                type="text" 
                                name="provincia" 
                                value={formData.provincia} 
                                onChange={handleChange} 
                                placeholder="Provincia"
                                list="provincia-list"
                                disabled={!formData.departamento}
                            />
                            {formData.departamento && provinciasPorDepartamento[formData.departamento] && (
                                <datalist id="provincia-list">
                                    {provinciasPorDepartamento[formData.departamento].map((p, i) => (
                                        <option key={i} value={p} />
                                    ))}
                                </datalist>
                            )}
                        </div>
                        
                        <div className="form-group">
                            <label>Distrito</label>
                            <input 
                                type="text" 
                                name="distrito" 
                                value={formData.distrito} 
                                onChange={handleChange} 
                                placeholder="Distrito"
                                disabled={!formData.provincia}
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>Dirección Completa</label>
                            <textarea 
                                name="address" 
                                value={formData.address} 
                                onChange={handleChange} 
                                placeholder="Calle, número, referencia..."
                                rows="3"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Botón de acción */}
            <div className="form-actions">
                <button type="submit" className="btn-submit">
                    {initialData ? '💾 Actualizar Cliente' : '✅ Crear Cliente'}
                </button>
                {formError && (
                    <p style={{ color: '#e74c3c', fontSize: '0.88rem', marginTop: '10px', textAlign: 'center' }}>
                        ⚠️ {formError}
                    </p>
                )}
            </div>
        </form>
    );
};

export default CustomerForm;